import React from 'react';
import { format } from 'date-fns';
import { ColumnDef } from '@tanstack/react-table';
import { 
  LucideEdit, 
  LucideTrash2, 
  MoreHorizontal, 
  CreditCard,
  FileText,
  Calendar,
  User,
  DollarSign,
  Tag,
  Building2
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Badge, BadgeProps } from '@/components/ui/badge';
import { DataTableColumnHeader } from '@/components/ui/data-table-column-header';

import { VoucherResponse } from '@/app/types/voucher';
import { getSimplifiedSalesInvoiceStatus, SimplifiedSalesInvoiceStatus } from '@/app/lib/statusUtils';

// Define custom actions for the Purchase Invoice
export const editPurchaseInvoiceEvent = 'editPurchaseInvoice';
export const deletePurchaseInvoiceEvent = 'deletePurchaseInvoice';
export const markAsPaidPurchaseInvoiceEvent = 'markAsPaidPurchaseInvoice';

// Define custom meta type
interface CustomColumnMeta {
  label?: string
  placeholder?: string
  variant?: 'text' | 'number' | 'select' | 'date' | 'dateRange' | 'boolean' | 'multiSelect'
  icon?: React.ComponentType<{ className?: string }>
  options?: Array<{ label: string; value: string | number | boolean }>
}

// Use intersection type for ExtendedColumnDef
type ExtendedColumnDef<TData, TValue = unknown> = ColumnDef<TData, TValue> & {
  meta?: CustomColumnMeta
}

// Define the columns for the Purchase Invoice DataTable
export const getColumns = (siteOptions: Array<{ label: string; value: string | number | boolean }> = []): ExtendedColumnDef<VoucherResponse>[] => [
  // Voucher Number Column
  {
    accessorKey: 'voucher_number',
    header: ({ column }) => <DataTableColumnHeader column={column} title="Invoice No." />,
    cell: ({ row }): JSX.Element => {
      const invoiceId = row.original.id
      const invoiceNumber = row.getValue('voucher_number') as string

      const handleClick = (e: React.MouseEvent): void => {
        e.stopPropagation() // Prevent any parent handlers
        
        // If Ctrl/Cmd is pressed, open in new tab
        if ((e.ctrlKey || e.metaKey) && window.api?.tabs) {
          e.preventDefault()
          window.api.tabs.create(
            `/purchase-invoices/detail/${invoiceId}`,
            `Invoice #${invoiceNumber}`
          )
        } else {
          // Navigate to detail page
          const event = new CustomEvent(editPurchaseInvoiceEvent, {
            detail: { purchaseInvoice: row.original }
          })
          document.dispatchEvent(event)
        }
      }

      return (
        <button
          onClick={handleClick}
          className="font-medium text-primary hover:underline focus:outline-none focus:underline"
          title="Click to view, Ctrl/Cmd+Click to open in new tab"
        >
          {invoiceNumber}
        </button>
      )
    },
    meta: {
      label: 'Invoice Number',
      placeholder: 'Filter invoices...',
      variant: 'text',
      icon: FileText
    },
    enableColumnFilter: true
  },
  // Reference Number Column (Supplier Invoice No.)
  {
    accessorKey: 'reference_number',
    header: ({ column }) => <DataTableColumnHeader column={column} title="Supplier Invoice No." />,
    cell: ({ row }) => {
      const refNo = row.getValue('reference_number') as string;
      return refNo || '-';
    },
    meta: {
      label: 'Reference',
      placeholder: 'Filter references...',
      variant: 'text',
      icon: FileText
    },
    enableColumnFilter: true
  },
  // Supplier Column
  {
    id: 'party.name',
    accessorKey: 'party.name',
    header: ({ column }) => <DataTableColumnHeader column={column} title="Supplier" />,
    cell: ({ row }) => {
      const party = row.original.party;
      return party?.name || '-';
    },
    meta: {
      label: 'Supplier',
      placeholder: 'Filter suppliers...',
      variant: 'text',
      icon: User
    },
    enableColumnFilter: true
  },
  // Site Column
  {
    id: 'site_id',
    accessorKey: 'site_id',
    header: ({ column }) => <DataTableColumnHeader column={column} title="Site" />,
    cell: ({ row }) => {
      const site = row.original.site;
      return site?.name || '-';
    },
    meta: {
      label: 'Site',
      placeholder: 'Filter by sites...',
      variant: 'multiSelect',
      icon: Building2,
      options: siteOptions
    },
    enableColumnFilter: true,
    enableHiding: true
  },
  // Transaction Date Column
  {
    accessorKey: 'transaction_date',
    header: ({ column }) => <DataTableColumnHeader column={column} title="Transaction Date" />,
    cell: ({ row }) => {
      const date = row.getValue('transaction_date') as string;
      return date ? format(new Date(date), 'dd/MM/yyyy') : '-';
    },
    meta: {
      label: 'Transaction Date',
      placeholder: 'Select date range...',
      variant: 'dateRange',
      icon: Calendar
    },
    enableColumnFilter: true
  },
  // Status Column
  {
    id: 'status',
    header: ({ column }) => <DataTableColumnHeader column={column} title="Status" />,
    cell: ({ row }) => {
      const invoice = row.original;
      const status = getSimplifiedSalesInvoiceStatus(invoice);
      
      let variant: BadgeProps['variant'] = 'default'
      let className = 'text-xs px-2 py-0.5 font-medium border-0 shadow-sm '
      
      switch (status) {
        case 'Paid':
          className += 'bg-gradient-to-r from-emerald-500 to-emerald-600 text-white'
          break
        case 'Partially Paid':
          className += 'bg-gradient-to-r from-amber-500 to-orange-600 text-white'
          break
        case 'Unpaid':
          className += 'bg-gradient-to-r from-slate-500 to-slate-600 text-white'
          break
        case 'Cancelled':
          className += 'bg-gradient-to-r from-rose-500 to-red-600 text-white'
          break
      }
      
      return <Badge variant={variant} className={className}>{status}</Badge>;
    },
    meta: {
      label: 'Status',
      placeholder: 'Filter by status...',
      variant: 'select',
      icon: Tag,
      options: [
        { label: 'Unpaid', value: 'Unpaid' },
        { label: 'Partially Paid', value: 'Partially Paid' },
        { label: 'Paid', value: 'Paid' },
        { label: 'Cancelled', value: 'Cancelled' }
      ]
    },
    enableColumnFilter: true
  },
  // Amount Column
  {
    accessorKey: 'total_amount',
    header: ({ column }) => <DataTableColumnHeader column={column} title="Amount" />,
    cell: ({ row }) => {
      const amount = parseFloat(row.getValue('total_amount') as string);
      return <span className="font-medium text-right">{new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
      }).format(amount)}</span>;
    },
    meta: {
      label: 'Amount',
      placeholder: 'Filter by amount...',
      variant: 'number',
      icon: DollarSign
    },
    enableColumnFilter: true
  },
];

// Export default columns for backward compatibility
export const columns = getColumns(); 